// src/pages/Enseignants/ListingAdherents/components/AdherentsTable.jsx
import React, { useState } from "react";
import { FaEllipsisV } from "react-icons/fa";
import { statusColor, getDefaultPhoto as utilDefaultPhoto } from "./utils";
import ButtonInfo from "../../../components/Button/ButtonInfo";
import ButtonEdit from "../../../components/Button/ButtonEdit";
import ButtonManageGroup from "../../../components/Button/ButtonManageGroup";

const formatDOB = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR");
};


  const toAbsolute = (u) => {
    if (!u) return "";
    const s = String(u).trim();
    // déjà absolu, data URL ou blob
    if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:")) return s;
    // chemins d'assets front (ex: /img/...): on laisse tel quel
    if (!s.startsWith("/uploads")) return s;
    // fichiers uploadés côté API
    const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
    return `${base}${s}`;
  };
  
const fallbackPhoto = (a, getDefaultPhoto) => {
  if (a?.autorisation_photo === false) return "/img/interdiction.jpg";
  if (a?.photo_url) return toAbsolute(a.photo_url);
  return getDefaultPhoto ? getDefaultPhoto(a?.genre) : utilDefaultPhoto(a?.genre);
};

// utilitaires de stop propagation
const stop = (e) => {
  e.preventDefault?.();
  e.stopPropagation?.();
};
const stopAnd = (fn, arg) => (e) => {
  stop(e);
  fn?.(arg);
};

const AdherentsTable = ({
  adherents = [],
  onRowClick,
  onEdit,
  onManageGroups,
  isAdmin = true,
  getDefaultPhoto,
}) => {
  const [openId, setOpenId] = useState(null);
  const toggleMobileActions = (e, id) => {
    stop(e);
    setOpenId((curr) => (curr === id ? null : id));
  };

  return (
    <div className="rounded-xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 overflow-hidden">
      {/* ====== Desktop (tableau) ====== */}
      <div className="hidden md:block overflow-x-auto">
        <table className="table-zebra table w-full border-collapse">
          <thead>
            <tr className="text-slate-700 dark:text-slate-300">
              <th className="w-[56px]">Photo</th>
              <th className="w-[220px] lg:w-[280px]">Adhérent</th>
              <th className="w-[150px]">Date de naissance</th>
              <th className="w-[160px]">Statut</th>
              <th className="w-[220px] text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {adherents.map((a) => {
              const photo = fallbackPhoto(a, getDefaultPhoto);
              const nom = (a?.nom || "").toUpperCase();
              const prenom = a?.prenom || "";

              return (
                <tr
                  key={a.id}
                  className="hover bg-white/50 dark:bg-slate-900/40 "
                  onClick={() => onRowClick?.(a)}
                  role="button"
                >
                  {/* Photo */}
                  <td onClick={stop} onMouseDown={stop} onPointerDown={stop} className="align-middle">
                    <div className="avatar">
                      <div className="w-10 h-10 rounded-full ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
                        <img src={toAbsolute(photo)} alt={`${nom} ${prenom}` || "Adhérent"} />
                      </div>
                    </div>
                  </td>

                  {/* Adhérent */}
                  <td className="align-middle">
                    <div className="min-w-[160px] max-w-[280px] lg:max-w-[300px] ">
                      <div className="font-semibold text-slate-900 dark:text-gray-900  truncate">
                        {nom}
                      </div>
                      <div className="text-sm text-slate-600 dark:text-gray-700 leading-tight truncate">
                        {prenom}
                      </div>
                    </div>
                  </td>

                  {/* Date de naissance */}
                  <td className="align-middle text-slate-700 dark:text-gray-900">
                    {formatDOB(a?.date_naissance)}
                  </td>

                  {/* Statut */}
                  <td className="align-middle">
                    <span className={`px-2 py-1 rounded text-xs ${statusColor(a?.statut_inscription)}`}>
                      {a?.statut_inscription || "—"}
                    </span>
                  </td>

                  {/* Actions (pare-feu events + wrappers) */}
                  <td
                    className="align-middle text-right"
                    onClick={stop}
                    onMouseDown={stop}
                    onPointerDown={stop}
                    onKeyDown={stop}
                  >
                    <div className="inline-flex gap-2">
                      <ButtonInfo onClick={stopAnd(onRowClick, a)} />
                      {isAdmin && <ButtonEdit onClick={stopAnd(onEdit, a)} />}
                      {isAdmin && (
                        <ButtonManageGroup onClick={stopAnd(onManageGroups, a)} />
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {adherents.length === 0 && (
              <tr>
                <td colSpan={5} className="py-8 text-center text-slate-600 dark:text-slate-300">
                  Aucun adhérent à afficher.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ====== Mobile (liste) ====== */}
      <ul className="md:hidden divide-y divide-slate-200 dark:divide-slate-700">
        {adherents.length === 0 && (
          <li className="py-6 text-center text-slate-600 dark:text-slate-300">
            Aucun adhérent à afficher.
          </li>
        )}

        {adherents.map((a) => {
          const photo = fallbackPhoto(a, getDefaultPhoto);
          const nom = (a?.nom || "").toUpperCase();
          const prenom = a?.prenom || "";
          const open = openId === a.id;

          return (
            <li
              key={a.id}
              className="relative bg-white/60 dark:bg-slate-900/40"
              onClick={() => onRowClick?.(a)}
              role="button"
            >
              <div className="p-3 flex items-start gap-3">
                {/* Photo */}
                <div
                  className="shrink-0 w-12 h-12 rounded-full ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden"
                  onClick={stop}
                  onMouseDown={stop}
                  onPointerDown={stop}
                >
                  <img src={toAbsolute(photo)} alt={`${nom} ${prenom}`} className="w-full h-full object-cover" />
                </div>

                {/* Textes empilés */}
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {nom} {prenom}
                  </div>
                  <div className="mt-1 text-xs text-slate-700 dark:text-slate-300">
                    {formatDOB(a?.date_naissance)}
                  </div>
                  <div className="mt-1">
                    <span className={`px-2 py-0.5 rounded text-[11px] ${statusColor(a?.statut_inscription)}`}>
                      {a?.statut_inscription || "—"}
                    </span>
                  </div>
                </div>

                {/* bouton ⋮ */}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={(e) => toggleMobileActions(e, a.id)}
                    onMouseDown={stop}
                    onPointerDown={stop}
                    onKeyDown={(e) => e.key === "Enter" && toggleMobileActions(e, a.id)}
                    title={open ? "Fermer les actions" : "Ouvrir les actions"}
                    className="absolute top-3 right-3 z-20 rounded-full bg-slate-900/85 text-white w-9 h-9 flex items-center justify-center shadow-lg hover:scale-105 active:scale-95 transition"
                  >
                    <FaEllipsisV />
                  </button>
                )}
              </div>

              {/* panneau actions mobile */}
              {open && (
                <div
                  className="px-3 pb-3"
                  onClick={stop}
                  onMouseDown={stop}
                  onPointerDown={stop}
                  onKeyDown={stop}
                >
                  <div className="mt-1 flex justify-end gap-2">
                    <ButtonInfo onClick={stopAnd(onRowClick, a)} />
                    {isAdmin && <ButtonEdit onClick={stopAnd(onEdit, a)} />}
                    {isAdmin && (
                      <ButtonManageGroup onClick={stopAnd(onManageGroups, a)} />
                    )}
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AdherentsTable;
